import React from "react";
import PropTypes from "prop-types";
import { MenuItem, Select, TextField, InputLabel, FormControl, Autocomplete, Box, Button } from "@mui/material";
import {
  LABELS,
  INPUT_HEIGHT,
  INPUT_MIN_WIDTH,
  COMMON_INPUT_STYLES,
  FEATURE_OPTIONS,
  ICON_SIZE,
  MESSAGE_CONFIG,
} from "./constants";
import "./filterbar.scss";
import Icon from "../Icons/Icon";

export default function AdvancedFilters({
  messageType,
  feature,
  details,
  onMessageTypeChange,
  onFeatureChange,
  onDetailSearch,
  featureOptions = FEATURE_OPTIONS,
}) {
  const hasActiveFilters = Boolean(messageType) || Boolean(details) || feature?.length > 0;

  const handleClearFilters = () => {
    onMessageTypeChange?.("");
    onFeatureChange?.([]);
    onDetailSearch?.("");
  };

  return (
    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" className="adv-filters">
      {/* Message Type */}
      <FormControl size="small" sx={COMMON_INPUT_STYLES}>
        <InputLabel id="adv-type-label">{LABELS.MESSAGE_TYPE}</InputLabel>

        <Select
          labelId="adv-type-label"
          value={messageType || ""}
          label={LABELS.MESSAGE_TYPE}
          onChange={e => onMessageTypeChange?.(e.target.value)}
          sx={{
            height: INPUT_HEIGHT,
            borderRadius: "8px !important",
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            },
          }}
          renderValue={val => {
            const config = MESSAGE_CONFIG[val];

            return (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {config && <Icon src={config.icon} {...ICON_SIZE} fill={config.fill} />}
                <span>{config?.label ?? val}</span>
              </span>
            );
          }}
        >
          {Object.entries(MESSAGE_CONFIG).map(([value, config]) => (
            <MenuItem key={value} value={value}>
              <Icon src={config.icon} {...ICON_SIZE} fill={config.fill} style={{ marginRight: 8 }} />
              {config.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Feature */}
      <Autocomplete
        multiple
        size="small"
        options={featureOptions}
        value={feature || []}
        onChange={(e, newVal) => onFeatureChange?.(newVal)}
        clearOnBlur={false}
        openOnFocus
        sx={{
          ...COMMON_INPUT_STYLES,
          "& .MuiInputBase-root": {
            height: INPUT_HEIGHT,
            alignItems: "center",
          },
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={LABELS.FEATURE}
            placeholder={feature?.length ? "" : LABELS.SEARCH_FEATURE_PLACEHOLDER}
            size="small"
            InputProps={{
              ...params.InputProps,
              sx: {
                minHeight: INPUT_HEIGHT,
                alignItems: "center",
                paddingY: 0,
                minWidth: 265,
              },
            }}
          />
        )}
      />

      {/* Message Search */}
      <TextField
        size="small"
        placeholder={LABELS.SEARCH_MESSAGE_PLACEHOLDER}
        value={details || ""}
        onChange={e => onDetailSearch?.(e.target.value)}
        sx={{
          minWidth: INPUT_MIN_WIDTH,
          "& .MuiInputBase-root": {
            height: INPUT_HEIGHT,
          },
          "& input::placeholder": {
            color: "rgba(0, 0, 0, 0.7)", // darker placeholder
            opacity: 1, // override MUI default
          },
        }}
      />

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button
          size="small"
          color="error"
          variant="text"
          onClick={handleClearFilters}
          sx={{ height: INPUT_HEIGHT, whiteSpace: "nowrap" }}
        >
          Clear Filters
        </Button>
      )}
    </Box>
  );
}

AdvancedFilters.propTypes = {
  messageType: PropTypes.string,
  feature: PropTypes.array,
  details: PropTypes.string,
  onMessageTypeChange: PropTypes.func,
  onFeatureChange: PropTypes.func,
  onDetailSearch: PropTypes.func,
  featureOptions: PropTypes.arrayOf(PropTypes.string),
};
